/* global globalThis */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DatePicker as ANTDDatePicker } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Ensure moment locales are bundled (required for moment.locale(...) to take effect)
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/pt';
import 'moment/locale/pt-br';

import enUS from 'antd/es/date-picker/locale/en_US';
import frFR from 'antd/es/date-picker/locale/fr_FR';
import esES from 'antd/es/date-picker/locale/es_ES';
import itIT from 'antd/es/date-picker/locale/it_IT';
import ptPT from 'antd/es/date-picker/locale/pt_PT';
import { Defines } from '../../project/Defines';

/**
 * custom DataPicker outputing date with format YYYY/MM/DD
 * @param {object} props  dataItem, setDataItem, dataField
 */
export function DatePicker(props) {
    const { dataItem, setDataItem, dataField, min, max, showTime, ...rest } = props;
    const [isMobile, setIsMobile] = useState(globalThis.innerWidth <= 768);
    const pickerRef = useRef(null);
    const { i18n } = useTranslation();
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(globalThis.innerWidth <= 768);
        };
        
        globalThis.addEventListener('resize', handleResize);
        return () => globalThis.removeEventListener('resize', handleResize);
    }, []);

    const lang = (i18n.language || 'en').split('-')[0];
    const antdLocales = {
        en: enUS,
        fr: frFR,
        es: esES,
        it: itIT,
        pt: ptPT
    };
    const pickerLocale = antdLocales[lang] || enUS;

    useEffect(() => {
        // Ensure moment uses the same locale as the UI language
        moment.locale(lang);
    }, [lang]);
    
    const date_format = showTime ? Defines.Format.MomentDateTime : Defines.Format.MomentDate;

    const getMomentValue = useCallback(() => {
        const raw = dataItem?.[dataField];
        if (!raw) return null;

        // Prefer ISO UTC values stored to send to server (e.g. 2026-03-14T23:06:00Z)
        const isoUtc = moment.utc(raw, moment.ISO_8601, true);
        if (isoUtc.isValid()) {
            return isoUtc.local();
        }

        // Fallback: previously stored formatted strings (local)
        const formattedLocal = moment(raw, date_format, true);
        if (formattedLocal.isValid()) {
            return formattedLocal;
        }

        // Last resort
        const loose = moment(raw);
        return loose.isValid() ? loose : null;
    }, [dataField, dataItem, date_format]);

    const onChange = useCallback((momentValue) => {
        if (setDataItem) {
            let newData = { ...dataItem };
            // Store UTC ISO string for server, while displaying in local timezone
            newData[dataField] = momentValue ? momentValue.utc().format() : null;
            setDataItem(newData);
        }
    }, [dataField, dataItem, setDataItem]);

    const disableDate = useCallback((d) => {
        return (min && d.isBefore(min)) || (max && d.isAfter(max));
    }, [min, max]);

    const getPopupContainer = useCallback((trigger) => {
        if (!trigger) {
            return document.body;
        }
        // Find the nearest scrollable parent container
        let element = trigger.parentElement;
        while (element && element !== document.body) {
            const style = globalThis.getComputedStyle(element);
            const overflow = style.overflow;
            const overflowY = style.overflowY;
            // Check if element is scrollable
            if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
                return element;
            }
            // Also check for common scrollable containers by class name
            if (element.classList && (
                element.classList.contains('scroll_wrapper') || 
                element.classList.contains('scroll') ||
                element.classList.contains('view')
            )) {
                return element;
            }
            element = element.parentElement;
        }
        return document.body;
    }, []);

    return <ANTDDatePicker
        ref={pickerRef}
        {...rest}
        locale={pickerLocale}
        showTime={showTime}
        name={dataField}
        value={getMomentValue()}
        format={date_format}
        onChange={(momentValue) => onChange(momentValue)}
        disabledDate={disableDate}
        placement={isMobile ? 'topLeft' : 'bottomLeft'}
        getPopupContainer={getPopupContainer} 
        popupStyle={{ zIndex: 100000 }}
    />
}

DatePicker.propTypes = {
    dataItem: PropTypes.object.isRequired,
    setDataItem: PropTypes.func,
    dataField: PropTypes.string.isRequired,
    min: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
    max: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
    showTime: PropTypes.bool
};